import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";

// PrimeNG
import { ButtonModule } from "primeng/button";
import { DividerModule } from "primeng/divider";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { TooltipModule } from "primeng/tooltip";

// Feature Interfaces & Constants
import { CouponFilter, CouponFilterControls } from "../../interfaces";

/**
 * Coupons Filters Component
 * Sidebar filter panel for the coupons list
 * Based on customers-filters pattern
 */
@Component({
  selector: "app-coupons-filters",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    DividerModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
  ],
  template: `
    <div class="coupons-filters" [class.drawer-mode]="drawerMode">
      <!-- Header with Filters title and Clear All button -->
      <div class="filters-header-main" [ngClass]="drawerMode ? 'p-3' : 'mb-3'">
        <div class="header-content">
          <div class="header-left">
            <i class="pi pi-filter text-gray-600 mr-2"></i>
            <h2 class="filters-title">Filters</h2>
          </div>
          <!-- Clear All button -->
          <div class="header-right">
            <button
              class="clear-all-btn"
              [disabled]="loading || !hasActiveFilters()"
              [class.has-filters]="hasActiveFilters()"
              (click)="onClearFilters()"
              title="Clear all active filters"
            >
              Clear All
            </button>
          </div>
        </div>
      </div>

      <!-- Scrollable Filter Content Container -->
      <div
        class="filters-content-scroll"
        [ngClass]="drawerMode ? 'drawer-scroll' : 'default-scroll'"
      >
        <!-- Search Section -->
        <div
          class="search-section"
          [ngClass]="drawerMode ? 'px-3 py-3' : 'mb-4'"
        >
          <div class="search-input-wrapper">
            <p-iconfield iconPosition="left" styleClass="w-full">
              <p-inputicon>
                <i class="pi pi-search"></i>
              </p-inputicon>
              <input
                pInputText
                [formControl]="filterForm.controls.search"
                placeholder="Search by code or name..."
                [disabled]="loading"
                class="modern-search-input w-full"
              />
            </p-iconfield>
          </div>
        </div>

        <!-- Action Buttons Section -->
        <div
          class="filter-section action-section"
          [ngClass]="drawerMode ? 'px-3 pb-4' : 'mb-4'"
        >
          <div class="section-header mb-2">
            <h3 class="filter-section-title">Actions</h3>
          </div>

          <div class="csv-actions-row">
            <!-- Import CSV -->
            <button
              class="csv-action-btn import-btn"
              (click)="onImportClick()"
              [disabled]="loading"
              title="Import coupons from CSV"
            >
              <i class="pi pi-upload"></i>
              <span class="btn-label">Import</span>
            </button>

            <!-- Export CSV -->
            <button
              class="csv-action-btn export-btn"
              (click)="exportCoupons.emit()"
              [disabled]="loading"
              title="Export coupons to CSV"
            >
              <i class="pi pi-download"></i>
              <span class="btn-label">Export</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
        background-color: white;
        overflow: hidden;
        min-height: 0;
      }

      /* Main Header Section with Filters title and Clear All button */
      .filters-header-main {
        flex-shrink: 0;
        background: white;
        border-bottom: 1px solid #f1f5f9;

        .header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .header-left {
            display: flex;
            align-items: center;

            .filters-title {
              font-size: 1rem;
              font-weight: 600;
              color: #1e293b;
              margin: 0;
            }

            .pi-filter {
              font-size: 0.875rem;
            }
          }

          .header-right {
            .clear-all-btn {
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              padding: 0.375rem 0.75rem;
              font-size: 0.75rem;
              font-weight: 500;
              color: #9ca3af;
              cursor: pointer;
              transition: all 0.15s ease;
              outline: none;

              &.has-filters {
                background: #eff6ff;
                border-color: #3b82f6;
                color: #3b82f6;

                &:hover {
                  background: #dbeafe;
                  border-color: #2563eb;
                  color: #2563eb;
                  transform: translateY(-1px);
                  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
                }
              }

              &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
                transform: none;
                background: #f8fafc;
                border-color: #e2e8f0;
                color: #9ca3af;

                &:hover {
                  background: #f8fafc;
                  border-color: #e2e8f0;
                  color: #9ca3af;
                  transform: none;
                  box-shadow: none;
                }
              }

              &:not(.has-filters):not(:disabled):hover {
                background: #f1f5f9;
                border-color: #cbd5e1;
                color: #64748b;
              }
            }
          }
        }
      }

      /* Scrollable Content Container */
      .filters-content-scroll {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 0;
        height: 100%;

        &.drawer-scroll {
          height: calc(100vh - 80px);
          max-height: calc(100vh - 80px);
          padding: 0;
        }

        &.default-scroll {
          height: calc(100vh - 150px);
          max-height: calc(100vh - 150px);
          padding: 0 1rem;
        }

        /* Custom scrollbar styling */
        &::-webkit-scrollbar {
          width: 4px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 2px;

          &:hover {
            background: #9ca3af;
          }
        }

        scrollbar-width: thin;
        scrollbar-color: #d1d5db transparent;
      }

      /* Filter Sections */
      .filter-section {
        margin-bottom: 1rem;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid #f1f5f9;

        &:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 1rem;
        }

        .section-header {
          margin-bottom: 0.75rem;
        }

        .filter-section-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
          letter-spacing: -0.025em;
          text-transform: none;
        }
      }

      /* Search Section */
      .search-section {
        .search-input-wrapper {
          ::ng-deep {
            .p-iconfield {
              .p-inputicon {
                color: #9ca3af;
                transition: color 0.2s ease;
              }

              .p-inputtext {
                border: 1.5px solid #e5e7eb;
                border-radius: 8px;
                background: white;
                transition: all 0.2s ease;
                font-size: 0.875rem;

                &::placeholder {
                  color: #9ca3af;
                  font-weight: 400;
                }

                &:hover {
                  border-color: #d1d5db;
                }

                &:focus {
                  border-color: #3b82f6;
                  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                  outline: none;
                }
              }
            }
          }
        }
      }

      /* CSV Action Buttons - Compact inline style */
      .csv-actions-row {
        display: flex;
        gap: 0.5rem;
      }

      .csv-action-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.375rem;
        padding: 0.5rem 0.75rem;
        border-radius: 6px;
        font-size: 0.75rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s ease;
        flex: 1;
        min-width: 0;

        i {
          font-size: 0.75rem;
          flex-shrink: 0;
        }

        .btn-label {
          white-space: nowrap;
        }

        &:active:not(:disabled) {
          transform: translateY(0);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        // Import button - blue tint
        &.import-btn {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          color: #2563eb;

          &:hover:not(:disabled) {
            background: #dbeafe;
            border-color: #3b82f6;
            color: #1d4ed8;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(59, 130, 246, 0.15);
          }
        }

        // Export button - green tint
        &.export-btn {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #16a34a;

          &:hover:not(:disabled) {
            background: #dcfce7;
            border-color: #22c55e;
            color: #15803d;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(34, 197, 94, 0.15);
          }
        }
      }

      /* Drawer mode specific styles */
      .coupons-filters.drawer-mode {
        padding: 0;
        height: 100%;
        max-height: 100vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;

        .search-section {
          padding: 0.75rem 1rem;
          background: #fff;
          border-bottom: 1px solid #f1f3f4;
          flex-shrink: 0;

          .search-input-wrapper {
            ::ng-deep {
              .p-iconfield {
                width: 100%;

                .p-inputicon {
                  left: 0.75rem;
                  color: #9ca3af;
                }

                .p-inputtext {
                  width: 100%;
                  height: 2.5rem;
                  border: 1px solid #d1d5db;
                  border-radius: 8px;
                  padding: 0 1rem 0 2.5rem;
                  font-size: 0.875rem;
                  transition: all 0.2s ease;
                  background: white;

                  &::placeholder {
                    color: #9ca3af;
                    font-size: 0.875rem;
                  }

                  &:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                    outline: none;
                  }

                  &:hover {
                    border-color: #9ca3af;
                  }
                }
              }
            }
          }
        }

        .filter-section {
          padding: 0.75rem 1rem;
          background: #fff;
          border-bottom: 1px solid #f1f3f4;
          margin-bottom: 0;

          &:last-child {
            border-bottom: none;
          }

          .filter-section-title {
            font-size: 0.75rem;
            font-weight: 600;
          }
        }
      }

      /* Responsive adjustments */
      @media (max-width: 768px) {
        .filters-header-main {
          .header-content {
            .filters-title {
              font-size: 0.875rem;
            }

            .clear-all-btn {
              height: 1.75rem;
              font-size: 0.625rem;
              padding: 0 0.5rem;
            }
          }
        }

        .filters-content-scroll {
          &.drawer-scroll {
            max-height: calc(100vh - 100px);
          }
        }

        .csv-actions-row {
          gap: 0.375rem;
        }

        .csv-action-btn {
          padding: 0.375rem 0.5rem;
          font-size: 0.6875rem;
          gap: 0.25rem;

          i {
            font-size: 0.6875rem;
          }
        }

        .coupons-filters.drawer-mode {
          .search-section,
          .filter-section {
            padding: 0.5rem 0.75rem;
          }

          .filter-section {
            .filter-section-title {
              font-size: 0.75rem;
            }
          }
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CouponsFiltersComponent {
  @Input({ required: true }) filterForm!: FormGroup<CouponFilterControls>;
  @Input() loading = false;
  @Input() drawerMode = true;

  @Output() filtersChanged = new EventEmitter<Partial<CouponFilter>>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() createNew = new EventEmitter<void>();
  @Output() exportCoupons = new EventEmitter<void>();
  @Output() importCoupons = new EventEmitter<void>();

  /**
   * Check if there are active filters
   */
  hasActiveFilters(): boolean {
    const search = this.filterForm.controls.search.value;
    return !!(search && search.trim().length > 0);
  }

  /**
   * Clear all filters
   */
  onClearFilters(): void {
    this.clearFilters.emit();
  }

  /**
   * Open import dialog
   */
  onImportClick(): void {
    this.importCoupons.emit();
  }
}
